import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const ServiceAreaPagesNotRanking = () => {
  const symptoms = [
    "You have city pages but they don't rank for '[service] near [city]' searches",
    "Service area pages are essentially copies of each other with city names swapped",
    "Google Search Console shows minimal impressions for location-based keywords",
    "Competitors with physical locations outrank your service area pages",
    "Pages were indexed but have dropped out of search results",
    "You rank in your home city but not surrounding areas you serve"
  ];

  const rootCauses = [
    { cause: "Duplicate content across location pages", impact: "Google sees no unique value and may not index them" },
    { cause: "No local relevance signals", impact: "Pages don't demonstrate actual connection to the location" },
    { cause: "Missing local schema markup", impact: "Google can't verify your service area claims" },
    { cause: "No local testimonials or case studies", impact: "Nothing proves you actually serve that area" },
    { cause: "Thin content on location pages", impact: "Not enough information to rank competitively" },
    { cause: "Poor internal linking structure", impact: "Location pages lack authority from rest of site" },
    { cause: "No Google Business Profile connection", impact: "Missing the strongest local ranking signal" },
    { cause: "Targeting too many locations at once", impact: "Resources spread thin, nothing ranks well" }
  ];

  const fixSteps = [
    { step: "Create truly unique content for each location", details: "Reference local neighborhoods, landmarks, common lawn issues in that climate" },
    { step: "Add local testimonials to each page", details: "Feature reviews from customers in that specific city or area" },
    { step: "Include a local case study or before/after", details: "Show work you've actually done in that location" },
    { step: "Add LocalBusiness schema with service area", details: "Specify the areas you serve using areaServed property" },
    { step: "Build internal links from service pages", details: "Link 'lawn mowing' service page to all location pages offering that service" },
    { step: "Focus on 5-10 priority locations first", details: "Rank well in key areas before expanding to more locations" },
    { step: "Add embedded Google Map for each area", details: "Visual proof that you serve this location" }
  ];

  const faqs = [
    { question: "Why aren't my service area pages ranking?", answer: "Usually because they're duplicate content with just city names changed. Google sees these as low-quality pages and doesn't rank them. Each location page needs unique content that demonstrates real local relevance." },
    { question: "How do I make each location page unique?", answer: "Include location-specific content: local testimonials, case studies from that area, references to neighborhoods, mention of local conditions affecting lawns, and photos from jobs in that location. Each page should feel genuinely local." },
    { question: "How many service area pages should I create?", answer: "Start with 5-10 priority locations where you do the most business or have the least competition. It's better to rank well for 10 locations than to have 50 pages that don't rank at all." },
    { question: "Do I need a physical location to rank in a city?", answer: "No, but you need to prove you serve that area. Local testimonials, case studies, specific local content, and proper schema markup can help service area businesses rank even without a physical address there." },
    { question: "Should I use the same template for all location pages?", answer: "You can use a consistent structure, but the content must be unique. Same layout is fine; same text with city names swapped is not. Each page needs unique paragraphs, local references, and ideally local photos." },
    { question: "How long should service area pages be?", answer: "Aim for 600-1,000 words of unique content per page. Include: services offered in that area, local considerations, testimonials from that area, FAQs specific to local customers, and calls-to-action." },
    { question: "What schema markup do I need for service area pages?", answer: "Use LocalBusiness schema with areaServed property listing the cities/regions you serve. Also add Service schema for the services offered and FAQPage schema if you include an FAQ section." },
    { question: "How do I get local testimonials for each area?", answer: "Ask customers which city they're from when collecting reviews. Filter existing reviews by customer location. For areas without reviews yet, focus on content quality and add reviews as you complete jobs there." },
    { question: "Should I link to location pages from my homepage?", answer: "Yes, but thoughtfully. Link to your priority service areas from the homepage footer or a dedicated 'Areas We Serve' section. Each service page should also link to relevant location pages." },
    { question: "How long until service area pages start ranking?", answer: "With unique, quality content, you should see movement in 2-4 months. Competitive local markets may take longer. Consistently adding local content and earning reviews accelerates results." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/service-area-pages-local-seo/", label: "Service Area Pages Guide" },
    { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
    { to: "/website-fixes/why-your-service-pages-arent-ranking/", label: "Service Pages Not Ranking" }
  ];

  return (
    <>
      <SEO
        title="Service Area Pages Not Ranking? Here's Why | Leaf Rush Marketing"
        description="Are your location pages invisible in Google? Learn why service area pages fail to rank and how to fix them with unique local content."
        url="https://www.leafrushmarketing.com/website-fixes/service-area-pages-not-ranking/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/service-area-pages-not-ranking/"
        title="Service Area Pages Not Ranking? Here's Why"
        description="Are your location pages invisible in Google? Learn why service area pages fail to rank and how to fix them."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Service Area Pages Not Ranking? Here's Why
            </h1>
            <p className="text-xl text-muted-foreground">
              You created city pages for every area you serve, but they're invisible in search. Let's fix that.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Location Pages Are Failing" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Location Pages Don't Rank</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Get Your Location Pages Ranking" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Copy-Paste Location Page Problem</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Most lawn care businesses create service area pages by copying one page and changing the city name. Google has seen this pattern millions of times and knows it adds no value. <strong>These duplicate pages often get ignored entirely.</strong>
              </p>
              <p>
                The solution is local authenticity. Reference specific neighborhoods, show work you've done in that area, include testimonials from local customers, and mention local conditions that affect lawn care. This signals to Google that you genuinely serve each location.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, we build service area pages with unique local content for each city. Combined with proper schema markup and internal linking, these pages actually rank and generate local leads.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Location Pages Ranking" />
        </div>
      </article>
    </>
  );
};

export default ServiceAreaPagesNotRanking;
